import { useCallback, useRef, useState } from 'react';

interface FileDropzoneProps {
  onFile: (file: File) => void;
  onClear?: (file: null) => void;
  accept?: string;
  maxSizeMB?: number;
}

const ACCEPT_DEFAULT = '.pdf,.docx,.xlsx,.pptx';
const MAX_SIZE_DEFAULT = 50;

function getExtension(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() ?? 'FILE';
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileDropzone({
  onFile,
  onClear,
  accept = ACCEPT_DEFAULT,
  maxSizeMB = MAX_SIZE_DEFAULT,
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      setFileError(null);
      if (file.size > maxSizeMB * 1024 * 1024) {
        setFileError(`File exceeds ${maxSizeMB} MB limit`);
        return;
      }
      setSelectedFile(file);
      onFile(file);
    },
    [maxSizeMB, onFile]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = '';
    onClear?.(null);
  };

  return (
    <div>
      {selectedFile ? (
        <div className="file-list">
          <div className="file-item">
            <div className="file-item-icon">{getExtension(selectedFile.name)}</div>
            <span className="file-item-name">{selectedFile.name}</span>
            <span className="file-item-meta">{formatSize(selectedFile.size)}</span>
            <button
              className="file-item-remove"
              onClick={handleRemove}
              data-tip="Remove file"
              type="button"
              aria-label="Remove file"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`upload-zone${isDragging ? ' over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          aria-label="Upload file"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <div className="upload-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
          </div>
          <p className="upload-drop-title">Drag file here, or click to browse</p>
          <p className="t-sm t-muted">PDF, DOCX, XLSX, PPTX &nbsp;·&nbsp; Up to {maxSizeMB} MB</p>
        </div>
      )}
      {fileError && (
        <div className="alert alert-fail" style={{ marginTop: 'var(--s2)' }} role="alert">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {fileError}
        </div>
      )}
    </div>
  );
}
